// Maintenance kill switch (frontend half).
//
// On app boot, MaintenanceLayer reads GET /api/status. If the backend reports
// maintenance:true, it renders a full-screen maintenance page over the ENTIRE
// app — no auth, no routing, nothing else mounts. Flip it instantly (no rebuild)
// from Cloud Run:
//   gcloud run services update socialposts-backend --region=us-central1 \
//     --project=socialposts-ai-prod --update-env-vars=MAINTENANCE=on   (…=off to restore)
//
// Fails OPEN: if /api/status can't be reached (offline, backend cold-starting),
// getStatus() resolves to maintenance:false, so a flaky probe never locks anyone
// out. While the first probe is in flight we show a lightweight spinner — the
// same brief flash a returning user already sees while auth resolves.

import { useState, useEffect, type ReactNode } from 'react';
import { getStatus } from '@/lib/api';
import { Logo } from './Logo';
import { MeshBg } from './MeshBg';

const DEFAULT_MSG = "We're experiencing technical issues — we'll be back soon.";

function MaintenancePage({ message }: { message: string }) {
  return (
    <div className="min-h-screen relative overflow-hidden bg-surface flex items-center justify-center p-4">
      <MeshBg />
      <div className="relative z-10 w-full max-w-md text-center">
        <div className="flex justify-center mb-6">
          <Logo height={73} />
        </div>
        <div className="gradient-stroke bg-white rounded-2xl p-10 shadow-2xl shadow-primary/45">
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary text-2xl">
              <i className="ri-tools-line" />
            </span>
          </div>
          <h1 className="text-xl font-bold text-dark mb-3">Back soon</h1>
          <p className="text-dark-muted text-sm leading-relaxed">
            {message || DEFAULT_MSG}
          </p>
        </div>
        <p className="text-center text-xs text-dark-muted mt-6">
          Thanks for your patience.
        </p>
      </div>
    </div>
  );
}

export function MaintenanceLayer({ children }: { children: ReactNode }) {
  // undefined = first probe still in flight; then true/false.
  const [maint, setMaint] = useState<boolean | undefined>(undefined);
  const [message, setMessage] = useState<string>(DEFAULT_MSG);

  useEffect(() => {
    let cancelled = false;
    getStatus().then((s) => {
      if (cancelled) return;
      setMaint(s.maintenance);
      if (s.message) setMessage(s.message);
    });
    // Re-check periodically so the site comes back on its own once the flag is
    // flipped off — no user refresh needed. Cheap public probe every 30s.
    const id = setInterval(() => {
      getStatus().then((s) => {
        if (cancelled) return;
        setMaint(s.maintenance);
        if (s.message) setMessage(s.message);
      });
    }, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // First probe in flight — brief neutral spinner (same as auth-resolving flash).
  if (maint === undefined) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (maint) return <MaintenancePage message={message} />;
  return <>{children}</>;
}
